import { useDispatch, useSelector } from 'react-redux';

import {
    FormControl,
    RadioGroupCards,
    DispersedSourceIcon,
    ConcentrationsIcon,
    useTheme,
    InfoIcon,
    IconButton,
    Box,
    Typography,
    Tooltip
} from '@methanesat/ui-components';

import { useTranslate } from '../../hooks';
import {
    selectMethaneProduct,
    selectAreaEmissionsWithinZoom,
    selectEmissionsMapLayerConfigs,
    setAreaEmissionsProduct,
    setMethaneLayerConfig,
    selectPlatform
} from '../../reducers';
import type { RootState } from '../../store';
import { AreaEmissionsProducts, MethaneLayerIds, isAreaEmissionsProduct } from '../../types';
import { analytics, calculateDispersedIconColors, getAreaEmissionsIconColors } from '../../utils';

export const AreaEmissionsProductToggle = ({ location }: { location: string }) => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);
    const areaEmissionLayerEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.areaEmissionRaster].enabled
    );
    const areaEmissionsWithinZoom = useSelector(selectAreaEmissionsWithinZoom);

    const onProductChange = (newValue: string) => {
        if (!isAreaEmissionsProduct(newValue)) {
            throw new Error(`Invalid product: ${newValue}`);
        }

        analytics.toggleAreaEmissionsProduct({ source: location, value: newValue });
        dispatch(setAreaEmissionsProduct(newValue));

        // disable the point source layer when l3 is selected, enable if l4 is selected
        // layer can still be manually toggled on by users
        dispatch(
            setMethaneLayerConfig({
                layerId: MethaneLayerIds.plumeEmissionRate,
                layerUpdates: { enabled: newValue !== AreaEmissionsProducts.l3 }
            })
        );
    };

    const l4DispersedSourceIcon = (
        <DispersedSourceIcon
            colors={calculateDispersedIconColors(theme, areaEmissionLayerEnabled, AreaEmissionsProducts.l4, platform)}
        />
    );

    const concentrationsIcon = (
        <ConcentrationsIcon
            colors={getAreaEmissionsIconColors(theme, areaEmissionLayerEnabled, AreaEmissionsProducts.l3, platform)}
        />
    );

    const tooltipContent = (
        <Box>
            <Typography fontWeight="bold" fontSize=".9rem">
                {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.toggleToolTipTitle`)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
                {l4DispersedSourceIcon}
                <Typography fontSize=".7rem" fontWeight="bold" sx={{ paddingLeft: 0.5 }}>
                    {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l4Label`)}
                </Typography>
            </Box>
            <Typography fontSize=".7rem">
                {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l4ToggleToolTip`)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
                {concentrationsIcon}
                <Typography fontSize=".7rem" fontWeight="bold" sx={{ paddingLeft: 0.5 }}>
                    {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l3Label`)}
                </Typography>
            </Box>
            <Typography fontSize=".7rem">
                {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l3ToggleToolTip`)}
            </Typography>
        </Box>
    );

    return (
        <>
            {location === 'minilegend' && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <Typography fontWeight="bold" fontSize=".9rem" marginBottom={0.75}>
                        {t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.toggleTitle`)}
                    </Typography>
                    <Tooltip enterTouchDelay={0} leaveTouchDelay={3000} title={tooltipContent}>
                        <IconButton
                            size="small"
                            data-testid="dataset-toggle-tooltip-close"
                            sx={{ color: 'inherit', padding: '0px', marginLeft: '4px' }}
                        >
                            <InfoIcon
                                sx={{
                                    fontSize: '15px'
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            <FormControl disabled={!(areaEmissionLayerEnabled && areaEmissionsWithinZoom)}>
                <RadioGroupCards
                    ariaLabel={t(
                        `emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.ariaSelectEmissions`
                    )}
                    data-testid="emissions-product-radio-group"
                    value={product}
                    onChange={(newProduct) => onProductChange(newProduct)}
                    disabled={!(areaEmissionLayerEnabled && areaEmissionsWithinZoom)}
                    options={[
                        {
                            value: AreaEmissionsProducts.l4,
                            label: t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l4Label`),
                            icon: l4DispersedSourceIcon
                        },
                        {
                            value: AreaEmissionsProducts.l3,
                            label: t(`emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.l3Label`),
                            icon: concentrationsIcon
                        }
                    ]}
                />
            </FormControl>
        </>
    );
};
